import type { TranslationKey } from "~/i18n/translations";

/**
 * File upload error type
 */
type FileUploadError = {
  type: "duplicate" | "in-queue" | "validation";
  message: string;
  filenames: string[];
};

/**
 * Parses a FileUploadError to extract structured error information for display in modals.
 * Handles validation errors by categorizing them into file size, file type, and other errors.
 *
 * @param error - The FileUploadError to parse
 * @param t - Translation function for i18n
 * @returns Object containing title, message, and filenames for the error modal
 */
export function parseFileUploadError(
  error: FileUploadError,
  t: (key: TranslationKey, params?: Record<string, string | number>) => string
): {
  title: string;
  message: string;
  filenames: string[];
} {
  // Handle duplicate file errors
  if (error.type === "duplicate" && error.filenames.length > 0) {
    return {
      title: t("error_duplicate_file"),
      message: t("error_duplicate_file_in_knowledge", { filename: error.filenames[0] }),
      filenames: error.filenames,
    };
  }

  // Handle in-queue file errors
  if (error.type === "in-queue" && error.filenames.length > 0) {
    return {
      title:
        error.filenames.length === 1
          ? t("error_in_queue_file")
          : t("error_in_queue_files", { count: String(error.filenames.length) }),
      message:
        error.filenames.length === 1
          ? t("error_in_queue_file_desc", { filename: error.filenames[0] })
          : t("error_in_queue_files_desc", { count: String(error.filenames.length) }),
      filenames: error.filenames,
    };
  }

  // Handle validation errors
  if (error.type === "validation") {
    const errorLines = error.message.split("\n");
    const fileSizeErrors: string[] = [];
    const fileTypeErrors: string[] = [];
    const otherErrors: Array<{ filename: string; errorMsg: string }> = [];

    // Parse each error line to categorize errors
    errorLines.forEach((line) => {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      if (match) {
        const [, filename, errorMsg] = match;
        if (errorMsg.includes("File size exceeds")) {
          fileSizeErrors.push(filename);
        } else if (errorMsg.includes("not supported") || errorMsg.includes("File type")) {
          fileTypeErrors.push(filename);
        } else {
          otherErrors.push({ filename, errorMsg });
        }
      }
    });

    // Determine title and message based on error types
    const title = t("error_file_upload_validation");
    let message = "";
    const filenames: string[] = [];

    if (fileSizeErrors.length > 0) {
      // Extract max size from error message (format: "File size exceeds maximum allowed size of 100MB")
      const maxSizeMatch = error.message.match(/(\d+)MB/);
      const maxSize = maxSizeMatch ? Number.parseInt(maxSizeMatch[1], 10) : 100;
      message =
        fileSizeErrors.length === 1
          ? t("error_file_size_too_large", { maxSize })
          : `${fileSizeErrors.length} files exceed the maximum size of ${maxSize}MB`;
      filenames.push(...fileSizeErrors);
    } else if (fileTypeErrors.length > 0) {
      // Extract file type from error message if available
      const fileTypeMatch = error.message.match(/File type '([^']+)'/);
      const fileType = fileTypeMatch ? fileTypeMatch[1] : "unknown";
      message =
        fileTypeErrors.length === 1
          ? t("error_file_type_unsupported", { fileType })
          : `${fileTypeErrors.length} files have unsupported file types`;
      filenames.push(...fileTypeErrors);
    } else if (otherErrors.length > 0) {
      // Generic validation error
      message = t("error_file_validation_failed");
      filenames.push(...otherErrors.map((e) => e.filename));
    } else {
      // Fallback for unparseable errors
      message = error.message;
    }

    return { title, message, filenames };
  }

  // Fallback for unknown error types
  return {
    title: t("error_file_upload_validation"),
    message: error.message,
    filenames: error.filenames || [],
  };
}
